import { FC, ReactElement, useCallback, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import ApexCharts, { ApexOptions } from "apexcharts";
import { Box, Stack, Typography } from "@mui/material";

import { stackBarChartConfig } from "@/config";
import { chartColors } from "@/constants";
import { formatNumberWithSuffix } from "@/utils";

interface IDutiesChart {
  data: { name: string; value: number }[];
}

const DutiesChart: FC<IDutiesChart> = ({ data = [] }): ReactElement => {
  const toggleSeries = useCallback((name: string) => {
    ApexCharts.exec("single-bar-horizontal-stack", "toggleSeries", name);
  }, []);

  const highlightSeries = useCallback((name: string) => {
    ApexCharts.exec("single-bar-horizontal-stack", "highlightSeries", name);
  }, []);

  const resetHighlightSeries = useCallback(() => {
    ApexCharts.exec("single-bar-horizontal-stack", "resetSeries");
  }, []);

  const series: ApexOptions["series"] = useMemo(() => {
    return (data ?? []).map(({ name, value }, index) => ({
      name,
      data: [value],
      color: chartColors[index],
    }));
  }, [data]);

  const safeSeries: ApexOptions["series"] = useMemo(() => {
    if (Array.isArray(series) && series.length > 0) return series;
    return [{ name: "", data: [0], color: chartColors[0] }];
  }, [series]);

  return (
    <>
      <ReactApexChart
        options={{
          ...stackBarChartConfig,
          chart: {
            ...stackBarChartConfig.chart,
            height: 44,
          },
          plotOptions: {
            ...stackBarChartConfig.plotOptions,
            bar: {
              ...stackBarChartConfig.plotOptions?.bar,
              barHeight: "70%",
            },
          },
        }}
        series={safeSeries}
        type="bar"
        width="100%"
        height={44}
      />
      <Stack mt={1.25} spacing={0.75}>
        {series.map(({ name, color, data }) => {
          return (
            <Stack
              key={name}
              component="div"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              sx={{
                cursor: "pointer",
                padding: "2px 6px",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              onClick={() => toggleSeries(name as string)}
              onMouseEnter={() => highlightSeries(name as string)}
              onMouseLeave={resetHighlightSeries}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Box sx={{ width: 12, height: 12, background: color, borderRadius: "2px" }} />
                <Typography variant="body2" fontWeight={500}>{name}</Typography>
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                ${formatNumberWithSuffix(data[0] as number)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </>
  );
};

export default DutiesChart;